import { useState } from "react";
import { toast } from "sonner";
import { Send } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

/**
 * Formulaire de contact dédié à un service spécifique.
 * Envoie vers /api/contact avec un request_type pré-rempli, un sujet et un placeholder personnalisés.
 *
 * Props :
 *  - requestType : "search" | "buyout" | "consignment" | ...
 *  - title       : titre du bloc formulaire
 *  - subject     : sujet envoyé automatiquement au backend
 *  - placeholder : hint du champ message pour guider le client
 *  - extraFields : liste optionnelle de champs additionnels [{ name, label, placeholder }]
 */
export default function ServiceContactForm({
  requestType,
  title = "Prendre contact",
  subject = "",
  placeholder = "Détaillez votre demande…",
  extraFields = [],
  testid,
}) {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", message: "",
    ...Object.fromEntries(extraFields.map((f) => [f.name, ""])),
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      // On concatène les champs bonus dans le message pour ne pas modifier le backend
      const extraSummary = extraFields
        .map((f) => (form[f.name] ? `${f.label} : ${form[f.name]}` : ""))
        .filter(Boolean)
        .join("\n");
      const finalMessage = extraSummary
        ? `${form.message || ""}\n\n— Détails —\n${extraSummary}`
        : form.message;

      await api.post("/contact", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        subject,
        message: finalMessage,
        request_type: requestType,
      });
      toast.success("Demande envoyée — nous vous répondons sous 24 h.");
      setSent(true);
      setForm({ name: "", email: "", phone: "", message: "", ...Object.fromEntries(extraFields.map((f) => [f.name, ""])) });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Une erreur est survenue");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div data-testid={`${testid}-success`} className="bg-[#1E1E1E] border border-[#C8A75A]/40 rounded-md p-8">
        <p className="text-[10px] uppercase tracking-[0.35em] text-[#C8A75A] mb-3">Message envoyé</p>
        <h3 className="font-display text-xl text-white mb-2">Merci pour votre confiance.</h3>
        <p className="text-white/70 text-sm">Un membre de l'équipe KV International vous recontacte sous 24 h.</p>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid={testid}
      className="bg-[#1E1E1E] border border-white/10 rounded-md p-8 hover:border-[#C8A75A]/30 transition-colors"
    >
      <p className="text-[10px] uppercase tracking-[0.35em] text-[#C8A75A] mb-3">{title}</p>
      <div className="grid md:grid-cols-2 gap-4">
        <Wrap label="Nom / Prénom" required>
          <Input required data-testid={`${testid}-name`} value={form.name} onChange={(e) => set("name", e.target.value)} className="bg-[#0D0D0D] border-white/10 text-white h-11" />
        </Wrap>
        <Wrap label="Email" required>
          <Input required type="email" data-testid={`${testid}-email`} value={form.email} onChange={(e) => set("email", e.target.value)} className="bg-[#0D0D0D] border-white/10 text-white h-11" />
        </Wrap>
      </div>
      <Wrap label="Téléphone">
        <Input data-testid={`${testid}-phone`} value={form.phone} onChange={(e) => set("phone", e.target.value)} className="bg-[#0D0D0D] border-white/10 text-white h-11" />
      </Wrap>
      {extraFields.length > 0 && (
        <div className="grid md:grid-cols-2 gap-4">
          {extraFields.map((f) => (
            <Wrap key={f.name} label={f.label}>
              <Input
                data-testid={`${testid}-${f.name}`}
                value={form[f.name]}
                onChange={(e) => set(f.name, e.target.value)}
                placeholder={f.placeholder || ""}
                className="bg-[#0D0D0D] border-white/10 text-white h-11"
              />
            </Wrap>
          ))}
        </div>
      )}
      <Wrap label="Message" required>
        <Textarea required rows={4} data-testid={`${testid}-message`} value={form.message} onChange={(e) => set("message", e.target.value)} placeholder={placeholder} className="bg-[#0D0D0D] border-white/10 text-white" />
      </Wrap>
      <Button type="submit" disabled={sending} data-testid={`${testid}-submit`} className="mt-6 bg-[#C8A75A] text-black hover:bg-white uppercase tracking-widest text-xs px-8 py-6 rounded-md w-full md:w-auto">
        {sending ? "…" : (<><Send className="w-4 h-4 mr-2" /> Envoyer ma demande</>)}
      </Button>
    </form>
  );
}

function Wrap({ label, required, children }) {
  return (
    <div className="mt-4">
      <label className="text-[10px] uppercase tracking-[0.25em] text-white/50 block mb-2">
        {label}{required && <span className="text-[#C8A75A]"> *</span>}
      </label>
      {children}
    </div>
  );
}
